import { useEffect, useState } from 'react';
import { css } from 'styled-components';
import { useIsLgPlusOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { format, parse } from 'date-fns';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { RoomPanelMobile } from '@/app/components/hotel-detail/rooms/RoomPanelMobile';
import { RoomPanelDesktop } from '@/app/components/hotel-detail/rooms/RoomPanelDesktop';
import { createHotelRoomClickedPayload } from '@/app/common/starTrack/events/hotelRoomClicked.ts';
import { createHotelRoomOptionClickedPayload } from '@/app/common/starTrack/events/hotelRoomOptionClicked.ts';
import { getCancellationPolicy, getRoomCancellationOptions } from './RoomPanelCommon';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import { getMealPlanCode } from './roomCommon';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useRoomPanelOpenModal } from './RoomPanelModalProvider';
import { useInitialParams } from '../InitialParamsProvider';
import { trackVisitEvent } from '../trackVisitEvents';
import { useOffersContext } from '../OffersProvider';
import { useFetchHotelPromotions } from '../hooks/useFetchHotelPromotions';
import { useCreateAsyncPricing } from '../PricingProvider';
import getSearchParams from '../../search/common/getSearchParams';
import { useCuriosity } from '../CuriosityProvider';

export type RoomPanelProps = {
  room: RoomType;
  isBestValue?: boolean;
  noRoomImages?: boolean;
} & React.HTMLAttributes<HTMLElement>;

export function RoomPanel(props: RoomPanelProps) {
  const { room, isBestValue, noRoomImages = false, ...rest } = props;

  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { openModal } = useRoomPanelOpenModal();
  const urlSearchParams = useUrlSearchParams();
  const { setSelectedRoom: setContextSelectedRoom, setSelectedMealPlan: setContextSelectedMealPlan } =
    useSelectedMealPlan();
  const { isTrain, children: numberOfChildren, infants, nights, accommodationOnly } = urlSearchParams;
  const initialParams = useInitialParams();
  const { staticData, commonData, searchInfoDetailData, localization } = usePageWrapperData();
  const { handleRoomChosen, starTrack } = useCommonClient();
  const { sendCuriosityRoomRedirectEvent } = useCuriosity();
  const { applyMealPlanFilter } = useOffersContext();

  const [selectedMealPlan, setSelectedMealPlan] = useState<MealPlanType>(
    room.configurations[room.selectedMealPlanIndex] ?? room.configurations[0]
  );

  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];

  const { offersData } = useOffersContext();
  const { data: hotelPromotions } = useFetchHotelPromotions();
  const { data: asyncPricing } = useCreateAsyncPricing();

  const isLgPlus = useIsLgPlusOrGreater();

  const onRoomClick = () => {
    starTrack.hotelRoomClick(
      createHotelRoomClickedPayload({
        searchId: searchId || '',
        initialParams,
        room,
        providerId: room.configurations[0].supplier?.id?.toString(),
      })
    );
    openModal(room);
    trackVisitEvent({
      t,
      pageSection: 'hdp.roomDetails',
      mealPlan: selectedMealPlan,
      roomName: room.name,
      checkin: offersData?.checkin || '',
      checkout: offersData?.checkout || '',
      hotelStaticData,
      initialParams,
      promotionType: hotelPromotions?.promotionType || '',
      urlSearchParams,
      pricingId: asyncPricing?.pricingId,
      staticData,
      commonData,
      localization,
      selectedFlight: accommodationOnly ? undefined : offersData?.transport?.transports[0],
      searchInfoDetailData,
    });
  };

  const onContinueClick = (mealPlan: MealPlanType) => {
    starTrack.hotelRoomClick(
      createHotelRoomClickedPayload({
        searchId: searchId || '',
        initialParams,
        room,
        providerId: room.configurations[0].supplier?.id?.toString(),
      })
    );

    starTrack.hotelRoomOptionClicked(
      createHotelRoomOptionClickedPayload({
        searchId: searchId || '',
        initialParams,
        room,
        mealPlan,
      })
    );

    const groupedCancellationPolicies = getRoomCancellationOptions(room.configurations || []);
    const cancellationKey = getCancellationPolicy(mealPlan?.cancellationPolicy || { type: 'UNKNOWN' });
    const mealPlansPerCancellationPolicy =
      groupedCancellationPolicies[cancellationKey].deferredPaymentDepositPerMealPlan.length;
    sendCuriosityRoomRedirectEvent({
      people_child: numberOfChildren,
      people_infant: infants,
      checkin_date: offersData?.checkin ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd') : '', // Format YYYY-MM-DD
      checkout_date: offersData?.checkout
        ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
        : '', // Format YYYY-MM-DD
      nights: offersData?.nights || nights,
      product_type: isTrain ? 'TH' : 'DP',
      cheapest_price: offersData?.cheapestFilteredMealPlan?.finalTotalPrice,
      cheapest_price_currency: localization.currency,
      room_id: room.roomId,
      room_has_photo: (room.images.length || 0) > 0 ? 1 : 0,
      room_options: `${Object.keys(groupedCancellationPolicies).length},${mealPlansPerCancellationPolicy}`,
      room_position: offersData?.rooms?.findIndex((currentRoom) => currentRoom.roomOfferId === room.roomOfferId) || 0,
      is_cheapest_room: offersData?.rooms?.[0]?.roomOfferId === room.roomOfferId ? 1 : 0,
      chosen_room_id: room.roomId,
      chosen_meal_plan_id: mealPlan.id,
      chosen_meal_plan_name: mealPlan.mealPlanName,
      chosen_cancellation_name: mealPlan.cancellationPolicy.type.toLowerCase().replace('_', ' '),
      cheapest_meal_plan_id: offersData?.cheapestFilteredMealPlan?.id,
      cheapest_meal_plan_name: offersData?.cheapestFilteredMealPlan?.mealPlanName,
      provider_id: mealPlan.supplier.id,
    });
    setContextSelectedRoom(room);
    setContextSelectedMealPlan(mealPlan);
    handleRoomChosen(
      room.roomOfferId,
      mealPlan.id,
      getMealPlanCode(mealPlan),
      offersData?.transport?.transports?.[0].id ?? ''
    );
  };

  const onSelectMealPlan = (mealPlan: MealPlanType) => {
    setSelectedMealPlan(mealPlan);
  };

  useEffect(() => {
    if (!applyMealPlanFilter) {
      setSelectedMealPlan(room.configurations[0]);
    }
  }, [applyMealPlanFilter]);

  if (room.configurations.length === 0) return null;

  return (
    <div
      {...rest}
      css={css`
        display: flex;
        flex-direction: column;
        background-color: white;
        box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
      `}
    >
      {!isLgPlus && (
        <RoomPanelMobile
          room={room}
          onRoomClick={onRoomClick}
          selectedMealPlan={selectedMealPlan}
          onContinueClick={onContinueClick}
          onSelectMealPlan={onSelectMealPlan}
          isBestValue={isBestValue}
        />
      )}

      {isLgPlus && (
        <RoomPanelDesktop
          room={room}
          noRoomImages={noRoomImages}
          selectedMealPlan={selectedMealPlan}
          onContinueClick={onContinueClick}
          onSelectMealPlan={onSelectMealPlan}
        />
      )}
    </div>
  );
}
